// All pickleball matches here are doubles (2v2), never singles.

// Fisher-Yates shuffle.
export function shuffle(arr) {
  var a = arr.slice();
  for (var i = a.length - 1; i > 0; i--) {
    var j = Math.floor(Math.random() * (i + 1));
    var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
  }
  return a;
}

export function pairKey(a, b) { return a < b ? a + "|" + b : b + "|" + a; }

function teamsMatch(a, b) {
  var a0 = a.slice().sort().join(","), b0 = b.slice().sort().join(",");
  return a0 === b0;
}

// `rounds` is an array of {teamA, teamB} objects rather than [teamA, teamB]
// tuples — Firestore rejects arrays nested directly inside arrays, so pairing
// history has to be stored as objects, not array-of-array-of-array.
function repeatsAnyRound(teamA, teamB, rounds) {
  for (var r = 0; r < rounds.length; r++) {
    var lr = rounds[r];
    if ((teamsMatch(lr.teamA, teamA) && teamsMatch(lr.teamB, teamB)) || (teamsMatch(lr.teamA, teamB) && teamsMatch(lr.teamB, teamA))) {
      return true;
    }
  }
  return false;
}

// Given 4 players already chosen for a court, pick whichever of the 3 possible
// 2v2 splits has partnered/faced each other the least so far, with a hard
// penalty against exactly repeating last round's matchup.
function bestSplitFor4(four, partnerCount, opponentCount, lastRoundTeams) {
  var splits = [
    [[four[0], four[1]], [four[2], four[3]]],
    [[four[0], four[2]], [four[1], four[3]]],
    [[four[0], four[3]], [four[1], four[2]]],
  ];
  var best = splits[0];
  var bestScore = Infinity;
  for (var i = 0; i < splits.length; i++) {
    var teamA = splits[i][0], teamB = splits[i][1];
    var score = (partnerCount[pairKey(teamA[0], teamA[1])] || 0) * 2
              + (partnerCount[pairKey(teamB[0], teamB[1])] || 0) * 2
              + (opponentCount[pairKey(teamA[0], teamB[0])] || 0)
              + (opponentCount[pairKey(teamA[0], teamB[1])] || 0)
              + (opponentCount[pairKey(teamA[1], teamB[0])] || 0)
              + (opponentCount[pairKey(teamA[1], teamB[1])] || 0);
    if (repeatsAnyRound(teamA, teamB, lastRoundTeams)) score += 100;
    if (score < bestScore) { bestScore = score; best = { teamA: teamA, teamB: teamB }; }
  }
  return best;
}

// Rolls a round's court assignments into the running fairness stats, ready to
// persist and pass back in next time a round is generated.
function applyRoundToStats(courts, stats) {
  var newGamesPlayed = { ...stats.gamesPlayed };
  var newPartnerCount = { ...stats.partnerCount };
  var newOpponentCount = { ...stats.opponentCount };
  var newLastRoundTeams = [];
  courts.forEach(function (c) {
    c.teamA.concat(c.teamB).forEach(function (uid) { newGamesPlayed[uid] = (newGamesPlayed[uid] || 0) + 1; });
    var pkA = pairKey(c.teamA[0], c.teamA[1]);
    var pkB = pairKey(c.teamB[0], c.teamB[1]);
    newPartnerCount[pkA] = (newPartnerCount[pkA] || 0) + 1;
    newPartnerCount[pkB] = (newPartnerCount[pkB] || 0) + 1;
    c.teamA.forEach(function (a) {
      c.teamB.forEach(function (b) {
        var ok = pairKey(a, b);
        newOpponentCount[ok] = (newOpponentCount[ok] || 0) + 1;
      });
    });
    newLastRoundTeams.push({ teamA: c.teamA, teamB: c.teamB });
  });
  return {
    gamesPlayed: newGamesPlayed,
    partnerCount: newPartnerCount,
    opponentCount: newOpponentCount,
    lastRoundTeams: newLastRoundTeams,
  };
}

// Picks one round of random-but-fair doubles pairings for social play, given
// the running `stats` accumulated over the whole session so far:
//   { gamesPlayed: {uid: n}, partnerCount: {"a|b": n}, opponentCount: {"a|b": n}, lastRoundTeams: [{teamA,teamB}, ...] }
// Two fairness rules drive it, addressing the two complaints directly:
//   1. Who plays this round is chosen by fewest games played so far (ties
//      broken randomly) — so nobody can sit out twice while someone else never
//      does; it self-corrects every round regardless of session length.
//   2. Within each court's group of 4, the team split is whichever has
//      partnered/faced each other the least so far — see bestSplitFor4.
// Returns { courts, sitOut, stats } — `stats` is the new running total to
// persist and pass back in next time this is called.
export function nextSocialRound(playerIds, courtCount, stats) {
  var s = stats || { gamesPlayed: {}, partnerCount: {}, opponentCount: {}, lastRoundTeams: [] };
  function played(uid) { return s.gamesPlayed[uid] || 0; }

  var ranked = shuffle(playerIds).sort(function (a, b) { return played(a) - played(b); });
  var maxCourts = Math.min(courtCount, Math.floor(playerIds.length / 4));
  var numPlaying = maxCourts * 4;
  var playing = ranked.slice(0, numPlaying);
  var sitOut = ranked.slice(numPlaying);

  var shuffledPlaying = shuffle(playing);
  var courts = [];
  for (var c = 0; c < maxCourts; c++) {
    var four = shuffledPlaying.slice(c * 4, c * 4 + 4);
    var best = bestSplitFor4(four, s.partnerCount, s.opponentCount, s.lastRoundTeams);
    courts.push({ court: c + 1, teamA: best.teamA, teamB: best.teamB });
  }

  return { courts: courts, sitOut: sitOut, stats: applyRoundToStats(courts, s) };
}

// Picks one round of skill-banded doubles pairings for league play. Unlike
// social mode, court groupings aren't random — each court's 4 players are
// pulled from a window of nearby ranks (roughly `windowSize` positions above
// or below a "seed" player), so matches stay competitive, but which players
// share a window shifts round to round as different people are due a game.
// That's the deliberate difference from a single static top/middle/bottom
// split for the whole night: over several rounds a player ends up facing a
// meaningfully wider slice of similarly-ranked people, not just the same
// fixed 4-5 all evening — and it reuses the exact same partner/opponent
// fairness tracking as social mode within each court's group of 4.
//
// `rankedPlayerIds` must already be sorted best -> worst (index 0 = best).
export function nextLeagueRound(rankedPlayerIds, courtCount, stats, windowSize) {
  var s = stats || { gamesPlayed: {}, partnerCount: {}, opponentCount: {}, lastRoundTeams: [] };
  var window = windowSize || 6;
  function played(uid) { return s.gamesPlayed[uid] || 0; }

  var rankIndex = {};
  rankedPlayerIds.forEach(function (uid, i) { rankIndex[uid] = i; });

  var maxCourts = Math.min(courtCount, Math.floor(rankedPlayerIds.length / 4));
  var numPlaying = maxCourts * 4;

  var priorityOrder = shuffle(rankedPlayerIds).sort(function (a, b) { return played(a) - played(b); });
  var playing = priorityOrder.slice(0, numPlaying);
  var sitOut = priorityOrder.slice(numPlaying);

  function withinWindow(pool, seedUid, maxDistance) {
    return pool.filter(function (uid) { return Math.abs(rankIndex[uid] - rankIndex[seedUid]) <= maxDistance; });
  }

  var remaining = playing.slice();
  var courts = [];
  for (var c = 0; c < maxCourts; c++) {
    if (remaining.length < 4) break;
    var seed = remaining[0];
    var pool = remaining.slice(1);

    var w = window;
    var candidates = [];
    while (candidates.length < 3 && w <= rankedPlayerIds.length) {
      candidates = withinWindow(pool, seed, w);
      w += window;
    }
    candidates = shuffle(candidates).sort(function (a, b) { return played(a) - played(b); });
    var four = [seed].concat(candidates.slice(0, 3));

    var nextRemaining = [];
    for (var i = 0; i < remaining.length; i++) {
      if (four.indexOf(remaining[i]) === -1) nextRemaining.push(remaining[i]);
    }
    remaining = nextRemaining;
    var best = bestSplitFor4(four, s.partnerCount, s.opponentCount, s.lastRoundTeams);
    courts.push({ court: c + 1, teamA: best.teamA, teamB: best.teamB });
  }
  sitOut = sitOut.concat(remaining);

  return { courts: courts, sitOut: sitOut, stats: applyRoundToStats(courts, s) };
}
